/* eslint-disable indent */
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { downloadPdfFile } from 'apis/projectProposals.api';
import PropTypes from 'prop-types';
import { toast } from 'layout';
import { TOAST } from 'constant';
import { downloadFile } from 'apis/project.api';
import { autoDownloadFile, formatFileName } from 'utils/func';
import React, { useMemo } from 'react';
import { downloadPdfFinancialAdjustment } from 'apis/financialAdjustment.api';
import { Button } from 'primereact/button';
import { downloadPersonnelAdjustmentPdfFile } from 'apis/personnelAdjustment.api';
import { downloadProjectDeadlineExtensionsPdfFile } from 'apis/projectDeadlineExtensions.api';

function ManagementAppendix({ projectProposalId, project, attachment, isReview }) {
  const { t } = useTranslation();

  const [extendImplementationTimeId, setExtendImplementationTimeId] = React.useState(null);
  const [financialAdjustmentId, setFinancialAdjustmentId] = React.useState(null);
  const [personnelAdjustmentId, setPersonnelAdjustmentId] = React.useState(null);

  const {
    mutate: mutateDownloadFile,
    isLoading: isDownloadContractLoading,
    isFetching: isDownloadContractFetching,
  } = useMutation(downloadFile);

  const {
    mutate: mutateDownloadPersonnelAdjustment,
    isLoading: isDownloadPdfAdjustMemberLoading,
    isFetching: isDownloadPdfAdjustMemberFetching,
  } = useMutation(downloadPersonnelAdjustmentPdfFile);

  const handleDownloadPersonnelAdjustment = (id) => {
    mutateDownloadPersonnelAdjustment(id, {
      onSuccess: async (res) => {
        autoDownloadFile(res);
      },
    });
  };

  const fileUrl = useMemo(() => project?.fileUrl ?? null, [project]);

  const extendImplementationTimes = useMemo(
    () => attachment?.extendImplementationTimes ?? [],
    [attachment]
  );

  const financialAdjustments = useMemo(() => attachment?.financialAdjustments ?? [], [attachment]);

  const personnelAdjustments = useMemo(
    () => attachment?.personnelAdjustments ?? false,
    [attachment]
  );

  const {
    mutate: downloadExplanationFileMutation,
    isLoading: isDownloadPdfFileLoading,
    isFetching: isDownloadPdfFileFetching,
  } = useMutation((id) => downloadPdfFile(id), {
    onSuccess: (res) => {
      autoDownloadFile(res);
    },
    onError: () => {
      toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
    },
  });

  const {
    mutate: downloadFinancialAdjustmentPdfFileMutation,
    isLoading: isDownloadFinancialAdjustmentPdfFileLoading,
    isFetching: isDownloadFinancialAdjustmentPdfFileFetching,
  } = useMutation((id) => downloadPdfFinancialAdjustment(id), {
    onSuccess: (res) => {
      autoDownloadFile(res);
    },
  });

  const handleDownloadContractFile = (id, path) => {
    mutateDownloadFile(
      { projectId: id, path },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = formatFileName(path);

            link.click();
          }
        },
      }
    );
  };

  const {
    mutate: mutateDownloadRenewFile,
    isLoading: isDownloadRenewFileLoading,
    isFetching: isDownloadRenewFileFetching,
  } = useMutation(downloadProjectDeadlineExtensionsPdfFile);

  const handleDownloadRenewFile = (id) => {
    mutateDownloadRenewFile(id, {
      onSuccess: async (res) => {
        autoDownloadFile(res);
      },
    });
  };

  return (
    <div className="mt-4">
      {isReview ? (
        <h4 className="mb-0">{t('submitDocument.summaryAndAppendix.explanationFile')}</h4>
      ) : (
        <h5 className="mb-0">{t('submitDocument.summaryAndAppendix.explanationFile')}</h5>
      )}

      <Button
        className="p-button p-button-text p-button-sm mt-1 p-0"
        onClick={() => {
          downloadExplanationFileMutation(projectProposalId);
        }}
        type="button"
        disabled={isDownloadPdfFileLoading || isDownloadPdfFileFetching}
      >
        <i className="pi pi-file-pdf text-red-500 text-xl" />{' '}
        <span className="text-blue-500 underline font-italic text-base ml-2">
          {isDownloadPdfFileLoading || isDownloadPdfFileFetching ? (
            <i className="pi pi-spin pi-spinner ml-2" />
          ) : (
            'Thuyet minh de tai'
          )}
        </span>
      </Button>
      {isReview ? (
        <h4 className="mb-0">{t('submitDocument.summaryAndAppendix.contractFile')}</h4>
      ) : (
        <h5 className="mb-0">{t('submitDocument.summaryAndAppendix.contractFile')}</h5>
      )}
      {fileUrl && (
        <span
          className="p-button p-button-text p-button-sm mt-1 p-0"
          onClick={() => handleDownloadContractFile(project?.id, fileUrl)}
        >
          <i className="pi pi-file-pdf text-red-500 text-xl" />{' '}
          <span className="text-blue-500 underline font-italic text-base ml-2">
            {isDownloadContractLoading || isDownloadContractFetching ? (
              <i className="pi pi-spin pi-spinner ml-2" />
            ) : (
              formatFileName(typeof fileUrl === 'string' ? fileUrl : fileUrl?.name)
            )}
          </span>
        </span>
      )}
      {extendImplementationTimes?.length > 0 && (
        <>
          {isReview ? (
            <h4 className="mb-0">{t('submitDocument.summaryAndAppendix.renewalDocumentsFile')}</h4>
          ) : (
            <h5 className="mb-0">{t('submitDocument.summaryAndAppendix.renewalDocumentsFile')}</h5>
          )}
          {extendImplementationTimes.map((item) => (
            <span
              key={item.id}
              className="p-button p-button-text p-button-sm mt-1 p-0"
              onClick={() => {
                setExtendImplementationTimeId(item.id);
                handleDownloadRenewFile(item?.id);
              }}
            >
              <i className="pi pi-file-pdf text-red-500 text-xl" />{' '}
              <span className="text-blue-500 underline font-italic text-base ml-2">
                {(isDownloadRenewFileLoading || isDownloadRenewFileFetching) &&
                extendImplementationTimeId === item.id ? (
                  <i className="pi pi-spin pi-spinner ml-2" />
                ) : (
                  formatFileName(
                    typeof item.fileUrl === 'string' ? item.fileUrl : item.fileUrl?.name
                  )
                )}
              </span>
            </span>
          ))}
        </>
      )}

      {personnelAdjustments?.length > 0 && (
        <>
          {isReview ? (
            <h4 className="mb-0">
              {t('submitDocument.summaryAndAppendix.humanResourcesUpdateFile')}
            </h4>
          ) : (
            <h5 className="mb-0">
              {t('submitDocument.summaryAndAppendix.humanResourcesUpdateFile')}
            </h5>
          )}
          {personnelAdjustments.map((item) => (
            <span
              className="p-button p-button-text p-button-sm mt-1 p-0"
              onClick={() => {
                setPersonnelAdjustmentId(item.id);
                handleDownloadPersonnelAdjustment(item.id);
              }}
            >
              <i className="pi pi-file-pdf text-red-500 text-xl" />{' '}
              <span className="text-blue-500 underline font-italic text-base ml-2">
                {(isDownloadPdfAdjustMemberLoading || isDownloadPdfAdjustMemberFetching) &&
                personnelAdjustmentId === item.id ? (
                  <i className="pi pi-spin pi-spinner ml-2" />
                ) : (
                  'Dieu chinh nhan su'
                )}
              </span>
            </span>
          ))}
        </>
      )}

      {financialAdjustments?.length > 0 && (
        <>
          {isReview ? (
            <h4 className="mb-0">
              {t('submitDocument.summaryAndAppendix.adjustmentExpenditureFile')}
            </h4>
          ) : (
            <h5 className="mb-0">
              {t('submitDocument.summaryAndAppendix.adjustmentExpenditureFile')}
            </h5>
          )}
          {financialAdjustments.map((item) => (
            <span
              key={item.id}
              className="p-button p-button-text p-button-sm mt-1 p-0"
              onClick={() => {
                setFinancialAdjustmentId(item.id);
                downloadFinancialAdjustmentPdfFileMutation(item.id);
              }}
            >
              <i className="pi pi-file-pdf text-red-500 text-xl" />{' '}
              <span className="text-blue-500 underline font-italic text-base ml-2">
                {(isDownloadFinancialAdjustmentPdfFileLoading ||
                  isDownloadFinancialAdjustmentPdfFileFetching) &&
                financialAdjustmentId === item.id ? (
                  <i className="pi pi-spin pi-spinner ml-2" />
                ) : (
                  'Dieu chinh kinh phi'
                )}
              </span>
            </span>
          ))}
        </>
      )}
    </div>
  );
}

ManagementAppendix.propTypes = {
  projectProposalId: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  project: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  attachment: PropTypes.array.isRequired,
  isReview: PropTypes.bool,
};

ManagementAppendix.defaultProps = {
  isReview: false,
};

export default React.memo(ManagementAppendix);
